import React, { useEffect, useState } from "react";
import { EmployeeSalary } from "../types/EmployeeSalary";
import { Form, Button, Row, Col } from "react-bootstrap";
import {
  MonthOptions,
  validateEmployeeId,
  YearOptions,
} from "../config/functions";
import { useDispatch, useSelector } from "react-redux";
import { StoreState } from "../redux/reducers";
import Select from "react-select";
import { fetchEmployeesAction } from "../redux/actions/employeeActions";
import "../styles/Form.css";
import { SalaryMaster } from "../types/salaryMaster";
import { fetchSalariesMasterAction } from "../redux/actions/salaryMasterAction";

interface Props {
  salaryIdToEdit?: number;
  initialData?: EmployeeSalary | null;
  onSave: (data: EmployeeSalary) => void;
  onEmployeeChange?: ((employeeId: string) => void) | null;
  salaryMaster: SalaryMaster | null;
}

const EmployeeSalaryForm: React.FC<Props> = ({
  salaryIdToEdit,
  initialData,
  onSave,
  onEmployeeChange,
  salaryMaster,
}) => {
  const [formData, setFormData] = useState<EmployeeSalary>({
    id: initialData?.id ?? 0,
    employeeId: initialData?.employeeId ?? "",
    year: initialData?.year ?? "",
    month: initialData?.month ?? "",
    gross: initialData?.gross ?? 0,
    bp: initialData?.bp ?? 0,
    hra: initialData?.hra ?? 0,
    tax: initialData?.tax ?? 0,
    pf: initialData?.pf ?? 0,
    ta: initialData?.ta ?? 0,
    sa: initialData?.sa ?? 0,
    lop: initialData?.lop ?? 0,
    netAmount: initialData?.netAmount ?? 0,
    createTimestamp: initialData?.createTimestamp ?? "",
  });

  const [employeeIdValid, setEmployeeIdValid] = useState<boolean | null>(null);
  const employees = useSelector((state: StoreState) => state.employee);
  const SalaryEmployee = useSelector(
    (state: StoreState) => state.salaryMaster.all,
  );
  const [selectedEmployeeOption, setSelectedEmployeeOption] =
    useState<SelectOption | null>(null);
  const dispatch = useDispatch<any>();

  type SelectOption = {
    label: string;
    value: string;
  };
  useEffect(() => {
    dispatch(fetchEmployeesAction());
    dispatch(fetchSalariesMasterAction());
  }, [dispatch]);

  const filteredEmployees = employees.filter((emp) =>
    SalaryEmployee.some(
      (sal) => String(sal.employeeId) === String(emp.employeeId),
    ),
  );
  console.log("fil", filteredEmployees);
  console.log("sa", SalaryEmployee);
  console.log("em", employees);

  useEffect(() => {
    if (formData.employeeId && salaryMaster) {
      setFormData((prev: any) => ({
        ...prev,
        gross: salaryMaster.gross,
        bp: salaryMaster.basicPay,
        hra: salaryMaster.hra,
        tax: salaryMaster.tax,
        pf: salaryMaster.pf,
        ta: salaryMaster.ta,
        sa: salaryMaster.sa,
      }));
    }
  }, [formData.employeeId, salaryMaster]);
  useEffect(() => {
    if (initialData?.month) {
      const matched = MonthOptions().find(
        (m) => m.label === initialData.month || m.value === initialData.month,
      );

      setFormData((prev) => ({
        ...prev,
        ...initialData,
        month: matched ? matched.value : "",
      }));
    }
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (/^\d*$/.test(value)) {
      let cleanValue = value.replace(/^0+/, "");

      setFormData({
        ...formData,
        [name]: cleanValue === "" ? "" : cleanValue,
      });
    }

    if (name === "employeeId") {
      validateEmployeeId(value).then(setEmployeeIdValid);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (employeeIdValid === false) return;
    onSave(formData);
  };
  const preventScroll = (e: React.WheelEvent<HTMLInputElement>) => {
    e.currentTarget.blur();
  };
  return (
    <Form onSubmit={handleSubmit}>
      <Row className="mb-1">
        <Col className="form-column">
          <Form.Group controlId="employeeId">
            <Form.Label>Employee ID</Form.Label>
            <Select
              options={filteredEmployees.map((emp) => ({
                label: emp.employeeId,
                value: emp.employeeId,
              }))}
              placeholder="Select Employee ID"
              value={
                filteredEmployees
                  .map((emp) => ({
                    label: emp.employeeId,
                    value: emp.employeeId,
                  }))
                  .find((opt) => opt.value === formData.employeeId) || null
              }
              onChange={(selectedOption) => {
                const selectedEmp = selectedOption as SelectOption;

                setFormData((prev) => ({
                  ...prev,
                  employeeId: selectedEmp ? selectedEmp.value : "",
                }));

                if (onEmployeeChange && selectedEmp) {
                  onEmployeeChange(selectedEmp.value);
                }
              }}
            />
          </Form.Group>
        </Col>
        <Col className="form-column">
          <Form.Group controlId="year">
            <Form.Label>Year</Form.Label>
            <Select
              name="year"
              options={YearOptions()}
              value={YearOptions().find(
                (option) => option.value === formData.year,
              )}
              onChange={(selectedOption) =>
                setFormData((prev) => ({
                  ...prev,
                  year: selectedOption ? String(selectedOption.value) : "",
                }))
              }
              placeholder="Select Option"
            />
          </Form.Group>
        </Col>
        <Col className="form-column">
          <Form.Group controlId="month">
            <Form.Label>Month</Form.Label>
            <Select
              name="month"
              options={MonthOptions()}
              value={
                MonthOptions().find(
                  (months) =>
                    months.value === formData.month ||
                    months.label === formData.month,
                ) || null
              }
              onChange={(selectedOption) =>
                setFormData((prev) => ({
                  ...prev,
                  month: selectedOption ? String(selectedOption.value) : "",
                }))
              }
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-1">
        <Col className="form-column">
          <Form.Group controlId="gross">
            <Form.Label>Gross (g)</Form.Label>
            <Form.Control
              type="number"
              name="gross"
              value={formData.gross}
              onChange={handleChange}
              readOnly
            />
          </Form.Group>
        </Col>
        <Col className="form-column">
          <Form.Group controlId="bp">
            <Form.Label>Basic Pay (BP)</Form.Label>
            <Form.Control
              type="number"
              name="bp"
              value={formData.bp}
              onChange={handleChange}
              readOnly
            />
          </Form.Group>
        </Col>
        <Col className="form-column">
          <Form.Group controlId="hra">
            <Form.Label>HRA</Form.Label>
            <Form.Control
              type="number"
              name="hra"
              value={formData.hra}
              onChange={handleChange}
              readOnly
            />
          </Form.Group>
        </Col>
        {/* <Col className="form-column">
          <Form.Group controlId="tax">
            <Form.Label>Tax</Form.Label>
            <Form.Control
              type="number"
              name="tax"
              value={formData.tax}
              onChange={handleChange}
              readOnly
            />
          </Form.Group>
        </Col> */}
      </Row>

      <Row className="mb-1">
        <Col className="form-column">
          <Form.Group controlId="pf">
            <Form.Label>Provident Fund (PF)</Form.Label>
            <Form.Control
              type="number"
              name="pf"
              value={formData.pf}
              onChange={handleChange}
              readOnly
            />
          </Form.Group>
        </Col>
        <Col className="form-column">
          <Form.Group controlId="ta">
            <Form.Label>Travel Allowance (TA)</Form.Label>
            <Form.Control
              type="number"
              name="ta"
              value={formData.ta}
              onChange={handleChange}
              readOnly
            />
          </Form.Group>
        </Col>
        <Col className="form-column">
          <Form.Group controlId="sa">
            <Form.Label>Special Allowance (SA)</Form.Label>
            <Form.Control
              type="number"
              name="sa"
              value={formData.sa}
              onChange={handleChange}
              readOnly
            />
          </Form.Group>
        </Col>
        <Col className="form-column">
          <Form.Group controlId="lop">
            <Form.Label>Loss of Pay (LOP)</Form.Label>
            <Form.Control
              type="text"
              name="lop"
              value={formData.lop}
              onChange={handleChange}
              onWheel={preventScroll}
              inputMode="numeric"
            />
          </Form.Group>
        </Col>
      </Row>

      {/* <Row className="mb-1"></Row> */}

      <Row className="mt-2">
        <Col md={12}>
          <div className="form-buttons">
            <Button
              variant="primary"
              type="submit"
              className="btn-save"
              disabled={employeeIdValid === false}
            >
              {initialData ? "Update Salary" : "Add Salary"}
            </Button>
          </div>
        </Col>
      </Row>
    </Form>
  );
};

export default EmployeeSalaryForm;
